// src/pages/admin/User_position.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  listPositions,
  createPosition,
  updatePosition,
  deletePosition,
  reorderPositions,
} from "../../api/users/positions";
import { listCompanies } from "../../api/users/companies";
import api from "../../api/axios";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const EMPTY_POS = { name: "" };

/* =========== 공통 로그 유틸 =========== */
const L = {
  info: (...a) =>
    console.log("%c[POSITION]", "color:#2563eb;font-weight:bold", ...a),
  warn: (...a) => console.warn("[POSITION]", ...a),
  err: (...a) => console.error("[POSITION]", ...a),
};
const parseAxiosError = (e) => {
  try {
    if (e && e.response && e.response.data) {
      return JSON.stringify(e.response.data);
    }
    return e && e.message ? e.message : String(e);
  } catch {
    return String(e);
  }
};

/* =========== 개별 행(정렬/수정) =========== */
function SortableRow({
  r,
  isReorder,
  editId,
  editData,
  setEditData,
  startEdit,
  handleUpdate,
  handleDelete,
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: r.id });
  const style = { transform: CSS.Transform.toString(transform), transition };

  return (
    <tr ref={setNodeRef} style={style} className="hover:bg-gray-50">
      <td className="border px-2 py-1 text-center">
        {isReorder && (
          <button
            {...attributes}
            {...listeners}
            className="cursor-grab px-2 py-1 border rounded bg-gray-50 active:cursor-grabbing mr-1"
            title="드래그해서 순서 변경"
          >
            ::
          </button>
        )}
        {r.id}
      </td>
      <td className="border px-2 py-1">
        {editId === r.id ? (
          <input
            type="text"
            value={editData.name}
            onChange={(e) =>
              setEditData((prev) => ({ ...prev, name: e.target.value }))
            }
            className="border rounded px-2 py-1 w-full"
          />
        ) : (
          r.name
        )}
      </td>
      <td className="border px-2 py-1 text-center">{r.level}</td>
      <td className="border px-2 py-1">
        {editId === r.id ? (
          <div className="flex gap-2">
            <button
              onClick={() => handleUpdate(r.id)}
              className="bg-green-500 hover:bg-green-600 text-white px-2 py-1 rounded"
            >
              저장
            </button>
            <button
              onClick={() => startEdit(null)}
              className="bg-gray-400 hover:bg-gray-500 text-white px-2 py-1 rounded"
            >
              취소
            </button>
          </div>
        ) : (
          <div className="flex gap-2">
            <button
              onClick={() => startEdit(r)}
              className="bg-yellow-500 hover:bg-yellow-600 text-white px-2 py-1 rounded"
            >
              수정
            </button>
            <button
              onClick={() => handleDelete(r.id)}
              className="bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded"
            >
              삭제
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}

/* =========== 메인 =========== */
const User_position = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");

  const [rows, setRows] = useState([]);
  const [newPos, setNewPos] = useState({ ...EMPTY_POS });
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({ ...EMPTY_POS });

  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [count, setCount] = useState(0);
  const totalPages = useMemo(
    () => (count > 0 ? Math.ceil(count / pageSize) : 1),
    [count, pageSize]
  );

  const [isReorder, setIsReorder] = useState(false);
  const [orderRows, setOrderRows] = useState([]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  // 선택 회사 id 정규화
  const selCompanyNum = selectedCompany ? Number(selectedCompany) : null;

  /* ----- 회사 목록 ----- */
  const loadCompanies = async () => {
    L.info("loadCompanies() 호출");
    try {
      const res = await listCompanies();
      const payload = res && res.data !== undefined ? res.data : res;
      const list = Array.isArray(payload)
        ? payload
        : payload && payload.results
        ? payload.results
        : [];
      L.info("회사 목록 로드 성공. count:", list.length, "payload:", payload);
      setCompanies(list);
    } catch (e) {
      L.err("회사 목록 불러오기 실패:", e);
      setCompanies([]);
    }
  };

  /* ----- 직급 목록 ----- */
  const loadPositions = async (companyId) => {
    L.info("loadPositions() 호출, companyId=", companyId);
    if (!companyId) {
      setRows([]);
      setCount(0);
      return;
    }
    try {
      // 첫 페이지 URL (필요하면 page_size 크게)
      let url = `positions/?company=${companyId}&page=1`;
      const all = [];
      let safety = 0;

      while (url && safety < 50) {
        const res = await api.get(url); // next가 절대경로여도 axios가 처리함
        const data = res?.data ?? {};
        const chunk = Array.isArray(data) ? data : data.results ?? [];
        L.info("직급 페이지 로드:", {
          url,
          count: chunk.length,
          next: data.next,
        });
        all.push(...chunk);
        // 다음 페이지: /api/ 이후만 남기면 프록시/베이스URL 이슈 줄어듦
        url = data.next ? data.next.replace(/^.*\/api\//, "") : null;
        safety += 1;
      }

      // 혹시 서버 필터가 허술하면 추가 필터
      const filtered = all.filter((r) => {
        const cid =
          (r.company_id !== undefined ? r.company_id : null) ||
          (r.company && typeof r.company === "object"
            ? r.company.id
            : r.company);
        return Number(cid) === Number(companyId);
      });

      setRows(filtered);
      setCount(filtered.length);
      L.info("직급 목록 최종 rows(모든 페이지 병합):", filtered);
    } catch (e) {
      L.err("직급 목록 불러오기 실패:", e);
      setRows([]);
      setCount(0);
    }
  };

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    // 회사 바뀔 때 상태 리셋 후 새로 로드
    L.info("회사 변경 감지 selectedCompany=", selectedCompany);
    setEditId(null);
    setIsReorder(false);
    setOrderRows([]);
    setPage(1);
    loadPositions(selectedCompany);
  }, [selectedCompany]);

  useEffect(() => {
    // 정렬 모드용 배열 준비
    if (isReorder) {
      const sorted = [...rows].sort((a, b) =>
        a.level !== b.level ? a.level - b.level : a.name.localeCompare(b.name)
      );
      setOrderRows(sorted);
      L.info("정렬 모드 ON: orderRows 세팅", sorted);
    }
  }, [isReorder, rows]);

  /* ----- 생성 ----- */
  const handleCreate = async () => {
    if (!selCompanyNum) {
      alert("회사를 먼저 선택하세요.");
      return;
    }
    if (!newPos.name || !newPos.name.trim()) {
      alert("직급명을 입력하세요.");
      return;
    }

    const payload = {
      name: newPos.name.trim(),
      // 백엔드가 외래키 필드명을 company로 받는 게 일반적. 필요 시 positions API 구현과 맞춰 조정
      company: selCompanyNum,
    };
    L.info("createPosition() 호출 payload:", payload);

    try {
      const resp = await createPosition(payload);
      L.info(
        "createPosition() OK resp:",
        resp && resp.data !== undefined ? resp.data : resp
      );
      setNewPos({ ...EMPTY_POS });
      await loadPositions(selCompanyNum);
      alert("직급이 추가되었습니다.");
    } catch (e) {
      const msg = parseAxiosError(e);
      L.err("createPosition() FAIL:", e);
      alert("직급 등록 실패: " + msg);
    }
  };

  /* ----- 수정 ----- */
  const handleUpdate = async (id) => {
    if (!editData.name || !editData.name.trim()) {
      alert("직급명을 입력하세요.");
      return;
    }
    const payload = {
      name: editData.name.trim(),
      company: selCompanyNum,
    };
    L.info("updatePosition() 호출, id=", id, "payload:", payload);
    try {
      const resp = await updatePosition(id, payload);
      L.info(
        "updatePosition() OK resp:",
        resp && resp.data !== undefined ? resp.data : resp
      );
      setEditId(null);
      await loadPositions(selCompanyNum);
      alert("수정되었습니다.");
    } catch (e) {
      const msg = parseAxiosError(e);
      L.err("updatePosition() FAIL:", e);
      alert("직급 수정 실패: " + msg);
    }
  };

  /* ----- 삭제 ----- */
  const handleDelete = async (id) => {
    if (!window.confirm("정말 삭제하시겠습니까?")) return;
    L.info("deletePosition() 호출, id=", id);
    try {
      const resp = await deletePosition(id);
      L.info(
        "deletePosition() OK resp:",
        resp && resp.data !== undefined ? resp.data : resp
      );
      await loadPositions(selCompanyNum);
      alert("삭제되었습니다.");
    } catch (e) {
      const msg = parseAxiosError(e);
      L.err("deletePosition() FAIL:", e);
      alert("직급 삭제 실패: " + msg);
    }
  };

  /* ----- 편집 시작/취소 ----- */
  const startEdit = (row) => {
    if (!row) {
      L.info("편집 취소");
      setEditId(null);
      return;
    }
    L.info("편집 시작 row:", row);
    setEditId(row.id);
    setEditData({ name: row.name || "" });
  };

  /* ----- DnD 정렬 ----- */
  const onDragEnd = (e) => {
    const active = e && e.active ? e.active : null;
    const over = e && e.over ? e.over : null;
    if (!over || !active || active.id === over.id) return;
    const from = orderRows.findIndex((r) => r.id === active.id);
    const to = orderRows.findIndex((r) => r.id === over.id);
    L.info("onDragEnd from->to:", from, "->", to);
    setOrderRows((prev) => arrayMove(prev, from, to));
  };

  const saveReorder = async () => {
    const ordered_ids = orderRows.map((r) => r.id);
    L.info("reorderPositions() 호출 ordered_ids:", ordered_ids);
    try {
      const resp = await reorderPositions(ordered_ids);
      L.info(
        "reorderPositions() OK resp:",
        resp && resp.data !== undefined ? resp.data : resp
      );
      setIsReorder(false);
      await loadPositions(selCompanyNum);
      alert("순서를 저장했습니다.");
    } catch (e) {
      const msg = parseAxiosError(e);
      L.err("reorderPositions() FAIL:", e);
      alert("레벨 재정렬 실패: " + msg);
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-xl font-bold mb-4">회사별 직급/역할 관리</h2>

      {/* 회사 선택 */}
      <div className="mb-4">
        <label className="mr-2">회사 선택:</label>
        <select
          value={selectedCompany}
          onChange={(e) => {
            L.info("회사 선택 변경 ->", e.target.value);
            setSelectedCompany(e.target.value);
          }}
          className="border px-2 py-1 rounded bg-white"
        >
          <option value="">-- 회사 선택 --</option>
          {companies.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      {/* 정렬 모드 */}
      <div className="mb-4 flex items-center gap-2">
        <button
          onClick={() => {
            L.info("정렬모드 토글:", !isReorder);
            setIsReorder((v) => !v);
          }}
          disabled={!rows.length}
          className={`px-3 py-2 rounded border disabled:opacity-50 ${
            isReorder ? "bg-gray-800 text-white" : ""
          }`}
        >
          {isReorder ? "정렬모드 종료" : "정렬모드"}
        </button>
        {isReorder && (
          <button
            onClick={saveReorder}
            className="px-3 py-2 rounded bg-blue-600 text-white"
          >
            순서 저장
          </button>
        )}
      </div>

      {/* 등록 */}
      <fieldset className="border border-gray-300 rounded p-4 mb-6">
        <legend className="text-lg font-semibold">직급 등록</legend>
        <div className="flex flex-col gap-2 max-w-md">
          <div className="text-sm text-gray-600">
            현재 선택 회사: <b>{selCompanyNum ? selCompanyNum : "미선택"}</b>
          </div>
          <input
            type="text"
            placeholder="직급명 *"
            value={newPos.name}
            onChange={(e) => {
              const v = e.target.value;
              setNewPos((prev) => ({ ...prev, name: v }));
            }}
            className="border rounded px-3 py-2 w-full"
          />
          <button
            onClick={handleCreate}
            disabled={!selectedCompany}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded disabled:opacity-50"
          >
            등록
          </button>
        </div>
      </fieldset>

      {/* 목록 */}
      <div className="overflow-x-auto">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onDragEnd}
        >
          <SortableContext
            items={(isReorder ? orderRows : rows).map((r) => r.id)}
            strategy={verticalListSortingStrategy}
          >
            <table className="min-w-full border border-gray-300 text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="border px-2 py-1 w-20">ID</th>
                  <th className="border px-2 py-1">직급명</th>
                  <th className="border px-2 py-1 w-24">레벨</th>
                  <th className="border px-2 py-1 w-40">관리</th>
                </tr>
              </thead>
              <tbody>
                {(isReorder ? orderRows : rows).map((r) => (
                  <SortableRow
                    key={r.id}
                    r={r}
                    isReorder={isReorder}
                    editId={editId}
                    editData={editData}
                    setEditData={setEditData}
                    startEdit={startEdit}
                    handleUpdate={handleUpdate}
                    handleDelete={handleDelete}
                  />
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={4} className="text-center py-4">
                      {selectedCompany
                        ? "데이터가 없습니다."
                        : "회사를 선택하세요."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
};

export default User_position;
